import L, { LocationEvent } from 'leaflet';
import 'leaflet.gridlayer.googlemutant';

import { wait } from "../../utils";
import { MarkerData } from '../../interfaces';

// Define missing types locally or cast to any
type AnyLeaflet = any;

export class CityMap {
    private config = window.MapEngineConfig;
    private mapDiv: HTMLDivElement;
    private map: L.Map;
    private layerSwitcher: HTMLDivElement;
    private layerButton: HTMLDivElement;
    private tileLayers: Map<string, L.Layer> = new Map<string, L.Layer>();
    private markerTypes: Map<string, L.Icon> = new Map<string, L.Icon>();
    private featureGroups: Map<string, L.FeatureGroup> = new Map<string, L.FeatureGroup>();
    private activeLayers: Set<L.FeatureGroup> = new Set<L.FeatureGroup>();
    private defaultLayersCount: number = this.config.filterGroups.length;
    private staticLayersCount: number = [].length;
    private localizationCircle: L.Circle | null = null;
    private localizationActive: boolean = false;
    private filteringActive: boolean = false;
    private markersByGroup: Map<string, Set<L.Marker>> = new Map();
    private clusterGroup!: any; // L.MarkerClusterGroup
    private isAutoSatelliteActive: boolean = this.config.autoSatellite.enabled;

    constructor(mapId: string, options?: any) {
        const defaultOptions: any = {
            attributionControl: false,
            scrollWheelZoom: false,
            smoothWheelZoom: true,
            smoothSensitivity: 5,
            zoomSnap: 0.25,
        };
        options = { ...defaultOptions, ...options };

        this.map = L.map(mapId, options);
        this.mapDiv = document.getElementById(mapId) as HTMLDivElement;
        this.layerSwitcher = document.getElementById('layer-switcher') as HTMLDivElement;
        this.layerButton = document.getElementById('layer-button') as HTMLDivElement;

        const osmTilelayer = L.tileLayer('https://{s}.tile.openstreetmap.de/tiles/osmde/{z}/{x}/{y}.png', {
            minZoom: 2,
            maxZoom: 18.5,
            subdomains: ['a', 'b', 'c'],
            attribution: '© <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, tiles: <a href="https://www.openstreetmap.de/">openstreetmap.de</a>'
        });

        const availableLayers: { [key: string]: L.Layer } = {
            'osm': osmTilelayer
        };

        // Only create Google layers if googleMutant is available (i.e., Google Maps API is loaded)
        if ((L.gridLayer as any).googleMutant) {
            availableLayers['googleRoadmap'] = (L.gridLayer as any).googleMutant({
                type: 'roadmap',
                styles: [
                    {
                        featureType: 'poi',
                        stylers: [{ visibility: 'off' }]
                    }
                ]
            });

            availableLayers['googleSatellite'] = (L.gridLayer as any).googleMutant({
                type: 'satellite',
                styles: [
                    {
                        featureType: 'poi',
                        stylers: [{ visibility: 'off' }]
                    }
                ]
            });

            availableLayers['googleHybrid'] = (L.gridLayer as any).googleMutant({
                type: 'hybrid',
                styles: [
                    {
                        featureType: 'poi',
                        stylers: [{ visibility: 'off' }]
                    }
                ]
            });
        }

        const baseLayers: { [key: string]: L.Layer } = {};
        let firstLayer: L.Layer | null = null;

        if (this.config.baseLayers) {
            this.config.baseLayers.forEach((layerKey: string) => {
                if (availableLayers[layerKey]) {
                    baseLayers[layerKey] = availableLayers[layerKey];
                    this.tileLayers.set(layerKey, availableLayers[layerKey]);
                    if (!firstLayer) {
                        firstLayer = availableLayers[layerKey];
                    }
                }
            });
        }

        // Fallback if no layers configured or found
        if (!firstLayer) {
            baseLayers['osm'] = osmTilelayer;
            this.tileLayers.set('osm', osmTilelayer);
            firstLayer = osmTilelayer;
        }

        firstLayer.addTo(this.map);

        this.initLayerSwitcher();

        L.control.attribution({
            position: 'topright'
        }).addTo(this.map);

        this.map.on('locationfound', (e: LocationEvent) => {
            const radius = e.accuracy / 2;

            if (this.localizationCircle) {
                this.map.removeLayer(this.localizationCircle);
            }
            this.localizationCircle = L.circle(e.latlng, {
                color: 'transparent',
                fillColor: '#0d6efd',
                fillOpacity: 0.3,
                radius: radius
            }).addTo(this.map);
        });

        this.map.on('locationerror', (err: any) => {
            console.warn("Error fetching location: ", err);
            this.toggleLocate(false);
        });

        // Failsafe check for bounds configuration
        try {
            if (this.config.bounds?.coordinates &&
                this.isValidLatLng(this.config.bounds.coordinates.northEast) &&
                this.isValidLatLng(this.config.bounds.coordinates.southWest)
            ) {
                console.log('fitting bounds');
                const bounds = L.latLngBounds(
                    L.latLng(
                        this.config.bounds.coordinates.southWest.lat,
                        this.config.bounds.coordinates.southWest.lng
                    ),
                    L.latLng(
                        this.config.bounds.coordinates.northEast.lat,
                        this.config.bounds.coordinates.northEast.lng
                    )
                );

                const padding = this.config.bounds.padding || 50;

                this.map.fitBounds(bounds, {
                    padding: [padding, padding]
                });
            } else {
                this.setDefaultView();
            }
        } catch (error) {
            console.warn('Error setting map bounds, falling back to default view:', error);
            this.setDefaultView();
        }

        if ((L as any).markerClusterGroup) {
            this.clusterGroup = (L as any).markerClusterGroup({
                chunkedLoading: true,
                spiderfyOnMaxZoom: true,
                showCoverageOnHover: true,
                maxClusterRadius: 5,
                animate: true,
                disableClusteringAtZoom: 19,
                iconCreateFunction: (cluster: any) => {
                    const count = cluster.getChildCount();
                    let size = 'small';

                    if (count > 100) {
                        size = 'large';
                    } else if (count > 10) {
                        size = 'medium';
                    }

                    return L.divIcon({
                        html: `<div>${count}</div>`,
                        className: `marker-cluster marker-cluster-${size}`,
                        iconSize: L.point(40, 40),
                        iconAnchor: L.point(20, 20)
                    });
                }
            });

            this.map.addLayer(this.clusterGroup);
        }
    }

    // Helper method to validate lat/lng values
    private isValidLatLng(coord: { lat: number, lng: number } | undefined): boolean {
        if (!coord) return false;

        const { lat, lng } = coord;

        return typeof lat === 'number' &&
            typeof lng === 'number' &&
            !isNaN(lat) &&
            !isNaN(lng) &&
            lat >= -90 &&
            lat <= 90 &&
            lng >= -180 &&
            lng <= 180;
    }

    // Helper method to set default view
    private setDefaultView(): void {
        console.log('setting default view');
        try {
            // Bounds für ganz Deutschland
            const germanyBounds = L.latLngBounds(
                L.latLng(47.27, 5.87),  // Südwest-Ecke
                L.latLng(55.06, 15.04)  // Nordost-Ecke
            );

            this.map.fitBounds(germanyBounds, {
                padding: [50, 50]
            });
        } catch (error) {
            console.error('Error setting default view, using hardcoded fallback:', error);
            // Absolute fallback mit Deutschland-Zentrum
            this.map.setView([51.1657, 10.4515], 6);
        }
    }

    public addMarker(lat: number, lng: number, data: MarkerData, onClickCallback?: () => void): L.Marker {
        const marker = L.marker([lat, lng], {
            icon: this.markerTypes.get(data.type),
        });

        if (data.popupContent) {
            marker.bindPopup(data.popupContent, {
                maxWidth: 200,
                maxHeight: 200,
                minWidth: 150
            });
        }

        if (onClickCallback) {
            marker.on('click', () => {
                onClickCallback()
                marker.openPopup();
            });
        }

        marker.on('mouseover', () => {
            marker.openPopup();
        });

        marker.on('mouseout', () => {
            marker.closePopup();
        });

        for (let group of data.filterGroups ?? []) {
            this.addMarkerToGroup(marker, group);
        }

        return marker;
    }

    public addMarkerType(name: string, path: string): void {
        const icon = L.icon({
            iconUrl: path,
            shadowUrl: '/res/public/files/image/icons/pin_shadow.svg',
            iconSize: [31, 41],
            shadowSize: [39, 41],
            iconAnchor: [15, 40],
            shadowAnchor: [6, 38],
            popupAnchor: [0, -41]
        });
        this.markerTypes.set(name, icon);
    }

    public addClickHandler(callback: (event: L.LeafletMouseEvent) => void): void {
        this.map.on('click', callback);
    }

    public addContextMenuHandler(callback: (event: L.LeafletMouseEvent) => void): void {
        this.map.on('contextmenu', callback);
    }

    public async panTo(lat: number, lng: number, zoom: number = 19, duration: number = 1): Promise<void> {
        this.map.flyTo([lat, lng], zoom, {
            animate: true,
            duration: duration
        });

        await wait(duration * 1000);
    }

    public async fitBoundsToFilterGroup(filterGroup: string, duration: number = 1): Promise<void> {
        const layer = this.featureGroups.get(filterGroup);
        if (layer) {
            this.map.flyToBounds(layer.getBounds(), {
                animate: true,
                duration: duration,
                padding: [50, 50],
            });
            await wait(duration * 1000);
        }
    }

    public async dodgeSidebar(dodge: boolean): Promise<void> {
        this.mapDiv.classList.toggle('dodge-sidebar', dodge);

        const fps = 30;
        const interval = 100 / fps;
        const duration = 300;

        const currentTime = Date.now();
        const endTime = currentTime + duration;

        while (Date.now() <= (endTime + interval)) {
            await wait(interval);
            this.map.invalidateSize();
        }
    }

    private addMarkerToGroup(marker: L.Marker, filterGroup: string): void {
        if (!this.markersByGroup.has(filterGroup)) {
            this.markersByGroup.set(filterGroup, new Set());
            this.featureGroups.set(filterGroup, L.featureGroup());
        }

        this.markersByGroup.get(filterGroup)?.add(marker);
        this.clusterGroup.addLayer(marker);
    }

    public showLayers(filterGroups: string[]): void {
        // Alle Marker aus der ClusterGroup entfernen
        this.clusterGroup.clearLayers();

        // Nur die Marker der aktiven Filtergruppen hinzufügen
        for (const [group, markers] of this.markersByGroup.entries()) {
            if (filterGroups.includes(group)) {
                markers.forEach(marker => {
                    this.clusterGroup.addLayer(marker);
                });
            }
        }
    }

    public toggleLayers(filterGroups: string[]): void {
        // Check if all filter groups to be toggled are currently active
        let wasActive = filterGroups.every(group =>
            Array.from(this.activeLayers).some(layer =>
                this.featureGroups.get(group) === layer
            )
        );

        // If this is the first filter being activated
        if (!this.filteringActive && this.config.filterGroups.length > 0) {
            // Clear all markers except static ones
            this.clusterGroup.clearLayers();

            // Add static markers back
            [].forEach(group => {
                const markers = this.markersByGroup.get(group);
                if (markers) {
                    markers.forEach(marker => {
                        this.clusterGroup.addLayer(marker);
                    });
                }
            });

            this.filteringActive = true;
        }

        // Toggle the specified filter groups
        for (let group of filterGroups) {
            const markers = this.markersByGroup.get(group);
            if (!markers) continue;

            const isActive = Array.from(this.activeLayers).some(layer =>
                this.featureGroups.get(group) === layer
            );

            if (isActive) {
                // Remove markers
                markers.forEach(marker => {
                    this.clusterGroup.removeLayer(marker);
                });
                this.activeLayers.delete(this.featureGroups.get(group)!);
            } else {
                // Add markers
                markers.forEach(marker => {
                    this.clusterGroup.addLayer(marker);
                });
                this.activeLayers.add(this.featureGroups.get(group)!);
            }
        }

        // Count active non-static layers
        const activeNonStaticLayers = Array.from(this.activeLayers).filter(layer => {
            const group = Array.from(this.featureGroups.entries())
                .find(([_, l]) => l === layer)?.[0];
            return group && !([] as string[]).includes(group);
        }).length;

        // If no non-static layers are active
        if (activeNonStaticLayers === 0) {
            this.filteringActive = false;
            this.showDefaultLayers();
        }

        // Always ensure static layers are shown
        this.showStaticLayers();
    }

    public showDefaultLayers(): void {
        this.clusterGroup.clearLayers();

        // Add default and static markers
        [...this.config.filterGroups, ...[]].forEach(group => {
            const markers = this.markersByGroup.get(group);
            if (markers) {
                markers.forEach(marker => {
                    this.clusterGroup.addLayer(marker);
                });
                this.activeLayers.add(this.featureGroups.get(group)!);
            }
        });
    }

    public showStaticLayers(): void {
        [].forEach(group => {
            const markers = this.markersByGroup.get(group);
            if (markers) {
                markers.forEach(marker => {
                    this.clusterGroup.addLayer(marker);
                });
                this.activeLayers.add(this.featureGroups.get(group)!);
            }
        });
    }

    public drawGpx(path: string): void {
        const gpx = new (L as any).GPX(path, {
            async: true,
            display_wpt: false,
            marker_options: {
                startIconUrl: null,
                endIconUrl: null,
                shadowUrl: null
            },
            polyline_options: {
                color: '#0d6efd',
                opacity: 0.75,
                weight: 5,
                lineCap: 'round'
            },
            //legacy map: polyline_options: {color: '#0f814d', weight: 5}
        });

        gpx.on('loaded', (e: any) => {
            this.map.fitBounds(e.target.getBounds(), {
                padding: [30, 30]
            });
        });

        gpx.addTo(this.map);
    }

    public toggleLocate(force?: boolean): boolean {
        if (force !== undefined) {
            this.localizationActive = force;
        } else {
            this.localizationActive = !this.localizationActive;
        }

        if (this.localizationActive) {
            this.map.locate({ setView: true, maxZoom: 16, watch: true, timeout: 10000 });
        } else {
            this.map.stopLocate();
            this.removeLocalizationCircle();
        }

        return this.localizationActive;
    }

    public isLocateActive(): boolean {
        return this.localizationActive;
    }

    public removeLocalizationCircle(): void {
        if (this.localizationCircle) {
            this.map.removeLayer(this.localizationCircle);
        }
    }

    public fitToBounds(bounds: {
        northEast: { lat: number, lng: number },
        southWest: { lat: number, lng: number }
    }): void {
        if (this.isValidLatLng(bounds.northEast) && this.isValidLatLng(bounds.southWest)) {
            const leafletBounds = L.latLngBounds(
                L.latLng(bounds.southWest.lat, bounds.southWest.lng),
                L.latLng(bounds.northEast.lat, bounds.northEast.lng)
            );

            const padding = this.config.bounds?.padding || 50;

            this.map.fitBounds(leafletBounds, {
                padding: [padding, padding]
            });
        }
    }

    private initLayerSwitcher(): void {
        const switcher = document.getElementById('layer-switcher');
        const button = document.getElementById('layer-button');

        if (!switcher || !button) return;

        // Check if we only have one layer configured - if so, hide the switcher
        if (this.config.baseLayers.length <= 1) {
            button.style.display = 'none';
            switcher.style.display = 'none';
            return;
        }

        // Toggle switcher visibility
        button.addEventListener('click', (e) => {
            e.stopPropagation();
            switcher.classList.toggle('open');
        });

        // Close switcher when clicking outside
        document.addEventListener('click', (e) => {
            if (!switcher.contains(e.target as Node) && !button.contains(e.target as Node)) {
                switcher.classList.remove('open');
            }
        });

        // Auto-satellite zoom listener
        this.map.on('zoomend', () => {
            if (!this.isAutoSatelliteActive) return;

            const zoom = this.map.getZoom();
            const threshold = this.config.autoSatellite.zoomThreshold;
            const hybridLayer = this.tileLayers.get('googleHybrid');
            const roadmapLayer = this.tileLayers.get('googleRoadmap');

            if (zoom >= threshold) {
                if (hybridLayer && !this.map.hasLayer(hybridLayer)) {
                    this.map.addLayer(hybridLayer);
                    if (roadmapLayer) this.map.removeLayer(roadmapLayer);
                    this.updateLayerSwitcherUI();
                }
            } else {
                if (roadmapLayer && !this.map.hasLayer(roadmapLayer)) {
                    this.map.addLayer(roadmapLayer);
                    if (hybridLayer) this.map.removeLayer(hybridLayer);
                    this.updateLayerSwitcherUI();
                }
            }
        });

        this.renderLayerSwitcherItems();
    }

    private renderLayerSwitcherItems(): void {
        const switcher = document.getElementById('layer-switcher');
        if (!switcher) return;

        switcher.innerHTML = '';

        // Auto-Satellite Toggle
        const toggleContainer = document.createElement('div');
        toggleContainer.className = 'layer-item auto-satellite-toggle';
        toggleContainer.style.borderBottom = '1px solid #eee';
        toggleContainer.style.marginBottom = '5px';
        toggleContainer.style.paddingBottom = '8px';
        toggleContainer.innerHTML = `
            <div style="flex-grow: 1; font-size: 0.9rem;">Autom. wechseln</div>
            <div class="form-check form-switch" style="margin: 0;">
                <input class="form-check-input" type="checkbox" id="autoSatelliteSwitch" ${this.isAutoSatelliteActive ? 'checked' : ''}>
            </div>
        `;
        switcher.appendChild(toggleContainer);

        const toggleInput = toggleContainer.querySelector('#autoSatelliteSwitch') as HTMLInputElement;
        toggleInput.addEventListener('change', (e) => {
            this.isAutoSatelliteActive = (e.target as HTMLInputElement).checked;
            // Trigger immediate check
            if (this.isAutoSatelliteActive) {
                this.map.fire('zoomend');
            }
        });

        // Layer Items
        this.config.baseLayers.forEach((layerKey: string) => {
            const config = this.config.layerConfig[layerKey];
            if (!config) return;

            const item = document.createElement('div');
            item.className = 'layer-item';
            item.dataset.layer = layerKey;

            const layer = this.tileLayers.get(layerKey);

            if (layer && this.map.hasLayer(layer)) {
                item.classList.add('active');
            }

            item.innerHTML = `
                <img src="${config.image}" class="layer-preview" alt="${config.label}">
                <span class="layer-label">${config.label}</span>
            `;

            item.addEventListener('click', () => {
                const layer = this.tileLayers.get(layerKey);

                if (layer && !this.map.hasLayer(layer)) {
                    // Disable auto-satellite on manual switch
                    this.isAutoSatelliteActive = false;
                    const toggle = document.getElementById('autoSatelliteSwitch') as HTMLInputElement;
                    if (toggle) toggle.checked = false;

                    // Remove other base layers
                    this.tileLayers.forEach(l => {
                        if (this.map.hasLayer(l)) this.map.removeLayer(l);
                    });

                    this.map.addLayer(layer);
                    this.updateLayerSwitcherUI();
                }
            });

            switcher.appendChild(item);
        });
    }

    private updateLayerSwitcherUI(): void {
        const items = document.querySelectorAll('.layer-item[data-layer]');
        items.forEach(item => {
            const layerKey = (item as HTMLElement).dataset.layer;
            if (layerKey) {
                const layer = this.tileLayers.get(layerKey);
                if (layer && this.map.hasLayer(layer)) {
                    item.classList.add('active');
                } else {
                    item.classList.remove('active');
                }
            }
        });
    }
}
